import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldAlert, History, X, CheckCircle2, Check } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { useAuth } from "@/context/CrmAuth";
import { Loader, Empty } from "@/crm/ui";
import { cn } from "@/lib/utils";
import {
  FLAG_META, RESULT_META, CARD, DOT, PILL, THEAD, TR,
  inputCls, btnPrimary, btnDanger, btnSuccess, iconBtn, fmtDate,
} from "./shared";
import Modal from "@/crm/components/Modal";

export default function FlaggedNumbers() {
  const { user } = useAuth();
  const isManager = ["admin", "manager"].includes(user?.role);
  const [items, setItems] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [form, setForm] = useState({ phone_number: "", status: "SURVEILLE", reason: "", notes: "" });
  const [history, setHistory] = useState(null);

  const load = useCallback(() => {
    api.get("/telephony/numbers", { params: { active: showAll ? "all" : "true" } })
      .then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [showAll]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.phone_number.trim()) return toast.error("Numéro requis");
    try {
      if (isManager) {
        await api.post("/telephony/numbers/flag", form);
        toast.success("Numéro enregistré");
      } else {
        await api.post("/telephony/numbers/report", form);
        toast.success("Signalement envoyé au manager pour validation");
      }
      setForm({ phone_number: "", status: "SURVEILLE", reason: "", notes: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const approve = async (it, status) => {
    try {
      await api.post(`/telephony/numbers/${it.id}/approve`, { status });
      toast.success(`Signalement validé → ${status}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const unflag = async (it) => {
    const reason = window.prompt("Motif du déblocage / retrait (obligatoire) :");
    if (!reason) return;
    try {
      await api.post(`/telephony/numbers/${it.id}/unflag`, { reason });
      toast.success("Numéro retiré de la liste");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openHistory = async (phone) => {
    try {
      const { data } = await api.get("/telephony/numbers/history", { params: { phone } });
      setHistory(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (items === null) return <Loader />;

  const actions = { openHistory, approve, unflag, isManager };

  return (
    <div data-testid="telephony-flags">
      {/* Marquer un numéro */}
      <div className={cn(CARD, "p-4 mb-3.5")}>
        <div className="text-[15px] font-medium text-slate-900">
          {isManager ? "Marquer un numéro" : "Signaler un numéro au manager"}
        </div>
        <p className="mt-1.5 mb-3.5 text-xs text-slate-400 text-pretty">
          {isManager
            ? "Surveillé (observation), Indésirable (routage spécial), Bloqué (règle de blocage). Liste distincte des oppositions « Ne plus contacter »."
            : "Un commercial ne peut pas bloquer définitivement un numéro : votre signalement sera validé (ou non) par le manager."}
        </p>
        <div className="grid gap-2.5 grid-cols-[repeat(auto-fit,minmax(170px,1fr))]">
          <input data-testid="flag-phone-input" className={cn(inputCls, "font-mono")} placeholder="06 12 34 56 78"
            value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} />
          {isManager && (
            <select data-testid="flag-status-select" className={inputCls} value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="SURVEILLE">Surveillé</option>
              <option value="INDESIRABLE">Indésirable</option>
              <option value="BLOQUE">Bloqué</option>
            </select>
          )}
          <input data-testid="flag-reason-input" className={inputCls} placeholder="Motif (ex : appels répétés)"
            value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <button data-testid="flag-submit-btn" onClick={submit} className={btnPrimary}>
            <ShieldAlert size={15} /> {isManager ? "Enregistrer" : "Signaler"}
          </button>
        </div>
      </div>

      {/* En-tête de liste + bascule historique */}
      <div className={cn(CARD, "overflow-hidden")}>
        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <span className="text-[13.5px] font-medium text-slate-700">Numéros signalés, surveillés et bloqués</span>
          <button
            type="button"
            data-testid="flag-show-all"
            aria-pressed={showAll}
            onClick={() => setShowAll((s) => !s)}
            className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700"
          >
            <span className={cn("h-[17px] w-[17px] rounded-[5px] border-[1.5px] flex items-center justify-center",
              showAll ? "bg-indigo-600 border-indigo-600" : "bg-white border-slate-300")}>
              {showAll && <Check size={11} strokeWidth={3.4} className="text-white" />}
            </span>
            Inclure l'historique (inactifs)
          </button>
        </div>

        {items.length === 0 ? (
          <Empty label="Aucun numéro signalé" icon={ShieldAlert} />
        ) : (
          <table className="w-full text-sm table-fixed hidden lg:table">
            <thead>
              <tr className={THEAD}>
                <th className="px-5 py-2.5 text-left">Numéro</th>
                <th className="px-2 py-2.5 text-left w-[148px]">Statut</th>
                <th className="px-2 py-2.5 text-left">Motif</th>
                <th className="px-2 py-2.5 text-left w-[152px]">Déclaré par</th>
                <th className="px-2 py-2.5 text-right w-[80px]">Appels</th>
                <th className="px-5 py-2.5 text-right w-[196px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`flag-row-${it.phone_number}`} className={cn(TR, !it.active && "opacity-55")}>
                  <td className="px-5 py-2.5 font-mono text-[13px] font-medium text-slate-800 truncate">{it.phone_number}</td>
                  <td className="px-2 py-2.5"><StatusPill it={it} /></td>
                  <td className="px-2 py-2.5 text-slate-600 truncate">{it.reason || "—"}</td>
                  <td className="px-2 py-2.5">
                    <div className="text-[13.5px] text-slate-500 truncate">{it.created_by_name}</div>
                    <div className="font-mono text-[11px] text-slate-400">{fmtDate(it.created_at)}</div>
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono text-slate-400">{it.call_count}</td>
                  <td className="px-5 py-2.5">
                    <div className="flex items-center justify-end gap-1.5"><RowActions it={it} {...actions} /></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Cartes en dessous de lg — flag-row-<numéro> existe dans les deux rendus */}
      <div className="flex flex-col gap-2.5 mt-2.5 lg:hidden">
        {items.map((it) => (
          <div key={it.id} data-testid={`flag-row-${it.phone_number}`} className={cn(CARD, "p-4", !it.active && "opacity-55")}>
            <div className="flex items-start justify-between gap-3">
              <span className="font-mono text-[13px] font-medium text-slate-800">{it.phone_number}</span>
              <StatusPill it={it} />
            </div>
            <div className="mt-3 text-sm text-slate-600 text-pretty">{it.reason || "—"}</div>
            <div className="mt-2 flex items-center justify-between gap-3 text-xs text-slate-400">
              <span className="truncate">{it.created_by_name} · {fmtDate(it.created_at)}</span>
              <span className="font-mono shrink-0">{it.call_count} appel(s)</span>
            </div>
            <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-slate-100">
              <RowActions it={it} {...actions} />
            </div>
          </div>
        ))}
      </div>

      {history && <HistoryModal history={history} onClose={() => setHistory(null)} />}
    </div>
  );
}

function StatusPill({ it }) {
  const meta = FLAG_META[it.status];
  return (
    <span className={cn(PILL, "border", meta?.cls || "bg-slate-100 text-slate-600 border-slate-200")}>
      <span className={cn(DOT, meta?.dot || "bg-slate-400")} />
      {meta?.label || it.status}{!it.active && " (retiré)"}
    </span>
  );
}

function RowActions({ it, isManager, openHistory, approve, unflag }) {
  return (
    <>
      <button data-testid={`flag-history-btn-${it.phone_number}`} onClick={() => openHistory(it.phone_number)}
        className={iconBtn} title="Historique" aria-label={`Historique de ${it.phone_number}`}>
        <History size={15} />
      </button>
      {isManager && it.active && it.status === "SIGNALE" && (
        <button data-testid={`flag-approve-btn-${it.phone_number}`} onClick={() => approve(it, "INDESIRABLE")} className={btnSuccess}>
          <CheckCircle2 size={13} /> Valider
        </button>
      )}
      {isManager && it.active && (
        <button data-testid={`flag-unflag-btn-${it.phone_number}`} onClick={() => unflag(it)} className={btnDanger}>
          Retirer
        </button>
      )}
    </>
  );
}

function HistoryModal({ history, onClose }) {
  return (
    <Modal onClose={onClose} size="2xl" testid="number-history-modal" label={history.phone_number}>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <div className="font-mono text-lg font-medium text-slate-900">{history.phone_number}</div>
          <div className="text-xs text-slate-400 mt-1">
            {history.call_count} appel(s) · durée totale {Math.round((history.total_duration || 0) / 60)} min ·
            dernier appel {fmtDate(history.last_call_at)}
          </div>
        </div>
        <button onClick={onClose} aria-label="Fermer" className="shrink-0 text-slate-400 hover:text-slate-600"><X size={18} /></button>
      </div>

      <div className="font-mono text-[10.5px] uppercase tracking-wider text-slate-400 mb-2">Journal des décisions</div>
      {history.decisions.length === 0 ? <p className="text-sm text-slate-400 mb-5">Aucune décision</p> : (
        <div className="flex flex-col gap-2 mb-5">
          {history.decisions.map((d) => (
            <div key={d.id} className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-sm">
              <b className="font-medium text-slate-800">{d.action}</b>
              <div className="text-xs text-slate-500 mt-0.5">
                {d.reason && <>Motif : {d.reason} · </>}Par {d.user_name} ({d.user_role}) · {fmtDate(d.created_at)}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="font-mono text-[10.5px] uppercase tracking-wider text-slate-400 mb-2">Historique des appels</div>
      {history.calls.length === 0 ? <p className="text-sm text-slate-400">Aucun appel</p> : (
        <div className="flex flex-col">
          {history.calls.map((c) => {
            const meta = RESULT_META[c.result];
            return (
              <div key={c.id} className="flex items-center justify-between gap-3 py-2 border-b border-slate-50 text-sm">
                <span className="font-mono text-xs text-slate-500 shrink-0">{fmtDate(c.created_at)}</span>
                <span className="text-xs text-slate-500 truncate">{c.final_destination || c.initial_destination || "—"}</span>
                <span className={cn(PILL, meta?.cls || "bg-slate-100 text-slate-600", "shrink-0")}>
                  <span className={cn(DOT, meta?.dot || "bg-slate-400")} />{meta?.label || c.result}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
